import React, { FC, useState, useCallback, useEffect, useRef } from "react";
import { Select, SelectProps, Option } from "@citi-icg-172888/icgds-react";
import _ from "lodash";
import { detectZoom } from "../services/utils/utils";

// Define a type for the value that Select and Option components use
export type SelectValue = string | number | (string | number | null | undefined);

interface ISelectHocProps
  extends Omit<
    SelectProps,
    "onChange" | "onSearch" | "value" | "defaultValue" | "onFocus" | "onBlur"
  > {
  options: string[];
  onChange?: (selectedOptions: SelectValue[]) => void;
  width?: string;
  isDisabled?: boolean;
  error?: string;
  label?: string;
  ariaLabel?: string;
  defaultValue?: SelectValue | SelectValue[];
  onSearch?: (searchTerm?: string) => void;
  value?: SelectValue | SelectValue[];
  ariaDescribedBy?: string | undefined;
  onFocus?: () => void;
  onBlur?: () => void;
  required?: boolean;
}

const TypedAdvancedSelectHoc: FC<ISelectHocProps> = (props) => {
  const {
    options,
    onChange,
    className,
    id,
    error,
    label,
    ariaLabel,
    defaultValue,
    onSearch,
    value,
    ariaDescribedBy,
    onFocus,
    onBlur,
    required, // Destructure the required prop
    ...rest
  } = props;

  const [selectedOptions, setSelectedOptions] = useState<SelectValue[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef<any>(null);
  const optionsRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize selected options from defaultValue
  useEffect(() => {
    if (defaultValue) {
      setSelectedOptions(
        Array.isArray(defaultValue) ? defaultValue : [defaultValue as SelectValue]
      );
    }
  }, [defaultValue]);

  // Initialize selected options from value prop
  useEffect(() => {
    if (value) {
      setSelectedOptions(Array.isArray(value) ? value : [value as SelectValue]);
    }
  }, [value]);

  useEffect(() => {
    const zoom = detectZoom();
    console.log("Current Zoom Level:", zoom); // Log zoom level
  }, []);

  // Handler for when the selected options change
  const onChangeHandler = useCallback(
    (selected: SelectValue[]) => {
      setSelectedOptions(selected);
      onChange && onChange(selected);
    },
    [onChange]
  );

  // Handler for keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex((prevIndex) => (prevIndex + 1) % options.length);
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex((prevIndex) => (prevIndex - 1 + options.length) % options.length);
          break;
        case "Enter":
          event.preventDefault();
          if (highlightedIndex >= 0) {
            onChangeHandler([options[highlightedIndex]]);
            setIsOpen(false);
            selectRef.current?.blur(); // Remove focus from the select after selection
          }
          break;
        case "Escape":
          setIsOpen(false);
          selectRef.current?.blur(); // Remove focus from the select on Escape
          break;
        default:
          break;
      }
    },
    [highlightedIndex, onChangeHandler, options, selectRef]
  );

  // Effect to focus the highlighted option
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && optionsRefs.current[highlightedIndex]) {
      optionsRefs.current[highlightedIndex]?.focus();
    }
  }, [highlightedIndex, isOpen]);

  // Accessibility properties
  const getAccessibilityProps = useCallback(() => {
    return {
      "aria-invalid": !!error,
      "aria-label": ariaLabel || label || "Select an option",
      "aria-describedby": ariaDescribedBy,
      "aria-expanded": isOpen,
      role: "combobox",
      "aria-haspopup": "listbox",
      "aria-required": !!required, // Add aria-required based on the required prop
    };
  }, [ariaDescribedBy, ariaLabel, error, id, isOpen, label, required]);

  // Handle search input changes
  const handleSearch = useCallback(
    (searchTerm?: string) => {
      onSearch && onSearch(searchTerm);
    },
    [onSearch]
  );
  const handleSelectFocus = useCallback(() => {
    setIsOpen(true);
    onFocus && onFocus(); // Call the parent's onFocus if provided
  }, [onFocus]);

  const handleSelectBlur = useCallback(() => {
    setIsOpen(false);
    onBlur && onBlur(); // Call the parent's onBlur if provided
  }, [onBlur]);

  return (
    <div className={className} onKeyDown={handleKeyDown}>
      <Select
        {...rest}
        id={id}
        ref={selectRef}
        size="default"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        value={selectedOptions}
        invalid={!!error}
        showSearch
        title={label || "Select an option"}
        onChange={onChangeHandler}
        onSearch={handleSearch}
        getPopupContainer={() => document.querySelector("#root")}
        onFocus={handleSelectFocus}
        onBlur={handleSelectBlur}// Use aria-required instead of required
        {...getAccessibilityProps()}
      >
        {_.map(options, (option) => (
          <Option key={option} label={option} aria-label={option} value={option}>
            {option}
          </Option>
        ))}
      </Select>
      
    </div>
  );
};

TypedAdvancedSelectHoc.defaultProps = {
  mode: "default",
  tagColor: "selected",
};

export default TypedAdvancedSelectHoc;